import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import {
  CreateProductPriceRequest,
  CustomUserPriceRequest,
  EditProductPriceRequest,
  RentalProductPrice,
} from '../../../../clients/quipli-service/pricing/pricing';
import { DatePicker } from '../../../../components/date-picker/date-picker';
import { EditableField, FieldLabel } from '../../../../components/field/field';
import { Modal } from '../../../../components/modal/modal';
import { displayMoneyFormat, safeRound } from '../../../../util/util';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { toast } from '../../../../util/toasts';
import { Typography } from '../../../../components/typography/typography';
import { OrderType } from '../../../../clients/quipli-service/order/order';
import { Button } from '../../../../components/form/button';
import { User } from '../../../../clients/quipli-service/user/user';
import { Product, ProductType } from '../../../../clients/quipli-service/product/product';
import { TextArea } from '../../../../components/textarea/textarea';
import { SearchProduct } from '../components/search-product';
import { usePricingApiV1 } from '../../../../clients/quipli-service/pricing/pricing-api-v1';
import { Spinner } from '../../../../components/spinner/spinner';

export const UserRentalPriceModal = ({
  user,
  currency,
  product,
  isOpen,
  close,
}: {
  user: User;
  currency: string;
  product?: Product;
  isOpen: boolean;
  close: (refresh?: boolean) => void;
}) => {
  const { createPrice, editPrice, deletePrice } = useAPI();
  const { listPrices, listPricesByUserId } = usePricingApiV1();

  const [selectedProduct, setSelectedProduct] = useState<Product | undefined>(product);
  const [productPrices, setProductPrices] = useState<RentalProductPrice[]>([]);
  // customRentalRatesForUser should be any modified rental rates
  const [customRentalRatesForUser, setCustomRentalRatesForCalc] = useState<{
    [durationStr: string]: CustomUserPriceRequest | undefined;
  }>({});
  const [startsOn, setStartsOn] = useState<DateTime | undefined>(undefined);
  const [endsOn, setEndsOn] = useState<DateTime | undefined>(undefined);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setSelectedProduct(product);
    }
  }, [product]);

  useEffect(() => {
    if (!selectedProduct) {
      return;
    }

    const fetchProductPrices = async (productId: number) => {
      setIsLoading(true);

      const [existingCustomerPrices, allPrices] = await Promise.all([
        listPricesByUserId(user.id).then((prices) =>
          prices.filter((p) => p.type === OrderType.Rental && p.productId === productId),
        ),
        listPrices({
          productIds: [productId],
          type: OrderType.Rental,
        }),
      ]).catch(() => {
        toast('Error retrieving product prices', 'error');
        return [[], []];
      });

      const startDates = existingCustomerPrices
        .map((price) => price.from)
        .filter((date) => date !== undefined) as DateTime[];
      const minStartsOn = startDates.reduce((min, date) => (date < min ? date : min), startDates[0]);
      setStartsOn(minStartsOn);
      const endDates = existingCustomerPrices
        .map((price) => price.to)
        .filter((date) => date !== undefined) as DateTime[];
      const maxEndsOn = endDates.reduce((max, date) => (date > max ? date : max), endDates[0]);
      setEndsOn(maxEndsOn);

      const overriddenPriceIds = (existingCustomerPrices as RentalProductPrice[])
        .map((r) => r.originalPrice?.id)
        .filter((id) => !!id);

      const prices = [
        ...(existingCustomerPrices as RentalProductPrice[]),
        ...(allPrices.filter((price) => !overriddenPriceIds.includes(price.id)) as RentalProductPrice[]),
      ].sort((a, b) => a.durationMinutes - b.durationMinutes);

      setProductPrices(prices);
      setIsLoading(false);
    };

    fetchProductPrices(selectedProduct.id);
  }, [selectedProduct]);

  useEffect(() => {
    const rentalRateUpdates: { [durationStr: string]: CustomUserPriceRequest } = {};
    for (const productPrice of productPrices.filter((price) => price.userId)) {
      const durationKey = `${productPrice.durationValue}-${productPrice.durationUnit}`;
      const existingUpdate = customRentalRatesForUser[durationKey];
      if (existingUpdate && existingUpdate.action !== 'remove') {
        rentalRateUpdates[durationKey] = {
          ...existingUpdate,
          from: startsOn,
          to: endsOn,
        };
      } else if (!existingUpdate) {
        rentalRateUpdates[durationKey] = {
          ...productPrice,
          from: startsOn,
          to: endsOn,
          action: 'update',
          idToChange: productPrice.id,
        };
      }

      setCustomRentalRatesForCalc({
        ...customRentalRatesForUser,
        ...rentalRateUpdates,
      });
    }
  }, [startsOn, endsOn]);

  const resetState = () => {
    setSelectedProduct(undefined);
    setProductPrices([]);
    setCustomRentalRatesForCalc({});
  };

  const isSaveDisabled: boolean = Object.keys(customRentalRatesForUser).length === 0;

  const savePrices = async () => {
    setIsSaving(true);

    for (const key in customRentalRatesForUser) {
      console.log(key, customRentalRatesForUser[key]);
      const priceRequest = customRentalRatesForUser[key];
      if (!priceRequest) {
        continue;
      }

      switch (priceRequest?.action) {
        case 'add':
          await createProductPrice(priceRequest);
          break;
        case 'update':
          await updateProductPrice(priceRequest);
          break;
        case 'remove':
          await deleteProductPrice(priceRequest);
          break;
      }
    }

    setIsSaving(false);
    resetState();
    close(true);
  };

  const createProductPrice = async (priceRequest: CustomUserPriceRequest) => {
    const payload = {
      type: OrderType.Rental,
      productId: priceRequest.productId,
      userId: user.id,
      durationUnit: priceRequest.durationUnit!,
      durationValue: priceRequest.durationValue!,
      price: +priceRequest.price!.toFixed(0),
      from: startsOn,
      to: endsOn,
      featured: true,
      locationIds: undefined,
    };

    try {
      await createPrice(payload as CreateProductPriceRequest);
    } catch (err) {
      toast('Error creating rental price', 'error');
    }
  };

  const updateProductPrice = async (priceRequest: CustomUserPriceRequest) => {
    const payload = {
      type: OrderType.Rental,
      productId: priceRequest.productId,
      userId: user.id,
      durationUnit: priceRequest.durationUnit!,
      durationValue: priceRequest.durationValue!,
      price: +priceRequest.price!.toFixed(0),
      from: startsOn,
      to: endsOn,
      featured: true,
      locationIds: undefined,
    };

    try {
      await editPrice(priceRequest.idToChange!, payload as EditProductPriceRequest);
    } catch (err) {
      toast('Error updating rental price', 'error');
    }
  };

  const deleteProductPrice = async (priceRequest: CustomUserPriceRequest) => {
    try {
      await deletePrice(priceRequest.idToChange!);
    } catch (err) {
      toast('Error removing rental price', 'error');
    }
  };

  const cancel = () => {
    resetState();
    close();
  };

  return (
    <Modal
      isOpen={isOpen}
      title={`${product === undefined ? 'Add' : 'Edit'} user rental prices`}
      close={cancel}
      className="rental-price-modal"
    >
      <div className="space-y-5 edit-rental-price-container font-sans">
        <div className="flex flex-col gap-[4px]">
          <FieldLabel label={'Product'} isRequired />
          {selectedProduct ? (
            <TextArea className="w-full h-full resize-none text-sm" value={selectedProduct.name} disabled={true} />
          ) : (
            <SearchProduct
              value={selectedProduct}
              onSelect={setSelectedProduct}
              productType={ProductType.Product}
              placeholder={'Search products'}
              isForRent={true}
            />
          )}
        </div>

        <div className="flex flex-col gap-[4px]">
          <FieldLabel label="Pricing Breakdown" />
          {productPrices.length > 0 ? (
            <div className={'flex-[0_1_calc(100%-20px)] show-prices'}>
              <table className={'invoice-table min-w-full'}>
                <thead>
                  <tr data-cy="rental_prices_table_headers">
                    {productPrices.map((price: RentalProductPrice) => {
                      return (
                        <th key={`rental-price-head-${price.productId}-${price.id}`} className="text-left pl-2">
                          <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
                            {price.durationLabel ?? 'Price'}
                          </Typography>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  <tr data-cy="rental_prices_table_prices_row">
                    {productPrices.map((price: RentalProductPrice) => {
                      const newCustomRentalRate =
                        customRentalRatesForUser[`${price.durationValue}-${price.durationUnit}`];
                      const previousRentalRate =
                        newCustomRentalRate && newCustomRentalRate?.price !== price.price ? price : undefined;
                      return (
                        <td key={`rental-price-body-${price.productId}-${price.id}`} className="p-2">
                          <Typography type="text-sm" className="text-quipli-black-800">
                            {previousRentalRate ? (
                              <s className="mr-1">{displayMoneyFormat(previousRentalRate.price / 100, currency)}</s>
                            ) : (
                              ''
                            )}
                          </Typography>

                          <EditableField
                            type="number"
                            value={newCustomRentalRate ?? price}
                            formattedValue={displayMoneyFormat(
                              safeRound((newCustomRentalRate?.price ?? price.price) / 100),
                              currency,
                            )}
                            canReset={previousRentalRate !== undefined}
                            resetPosition="right"
                            onReset={() => {
                              if (newCustomRentalRate) {
                                setCustomRentalRatesForCalc({
                                  ...customRentalRatesForUser,
                                  [`${price.durationValue}-${price.durationUnit}`]: undefined,
                                });
                              } else if (price.originalPrice) {
                                setCustomRentalRatesForCalc({
                                  ...customRentalRatesForUser,
                                  [`${price.durationValue}-${price.durationUnit}`]: {
                                    ...price,
                                    action: 'remove',
                                    idToChange: price.id,
                                  },
                                });
                              }
                            }}
                            onChange={(v) => {
                              setCustomRentalRatesForCalc({
                                ...customRentalRatesForUser,
                                [`${price.durationValue}-${price.durationUnit}`]: {
                                  ...price,
                                  price: v * 100,
                                  action:
                                    v * 100 === price.originalPrice?.price
                                      ? 'remove'
                                      : price.originalPrice?.price
                                      ? 'update'
                                      : 'add',
                                  idToChange:
                                    v * 100 === price.originalPrice?.price
                                      ? price.id
                                      : price.originalPrice?.price
                                      ? price.id
                                      : undefined,
                                },
                              });
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className={'flex-[0_1_calc(100%-20px)] show-prices'}>
              <table className={'invoice-table min-w-full'}>
                <thead>
                  <tr data-cy="rental_prices_table_headers">
                    <th className="text-left p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr data-cy="rental_prices_table_prices_row">
                    <td className="p-2">
                      <Typography type="text-sm" weight="regular" className="text-[var(--grey-500)]">
                        {selectedProduct ? (
                          isLoading ? (
                            <Spinner />
                          ) : (
                            'No prices available for this product.'
                          )
                        ) : (
                          'Please select a product'
                        )}
                      </Typography>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid grid-cols-5 items-center gap-2">
          <div className="col-span-2 ml-1">
            <span className="text-sm">Starts On</span>
          </div>
          <div className="col-span-3">
            <DatePicker value={startsOn} onChange={setStartsOn} clearable />
          </div>
        </div>

        <div className="grid grid-cols-5 items-center gap-2">
          <div className="col-span-2 ml-1">
            <span className="text-sm">Ends On</span>
          </div>
          <div className="col-span-3">
            <DatePicker value={endsOn} onChange={setEndsOn} clearable />
          </div>
        </div>
      </div>

      <div className="inset-x-0 bottom-0 mt-8 flex justify-end button-group">
        <Button className="button-secondary" onClick={cancel}>
          <Typography type="text-sm">Cancel</Typography>
        </Button>
        <Button className="button-primary" onClick={savePrices} disabled={isSaveDisabled || isSaving}>
          Save
        </Button>
      </div>
    </Modal>
  );
};
